import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiTriggerContactSearch,
  apiPollContactSearch,
  type ContactSearchType,
  type ContactSearchResponse,
  type ActurisContact,
} from 'api/quote-marketplace';

const POLL_INTERVAL = 2000;

interface UseContactSearchOptions {
  enabled?: boolean;
}

interface UseContactSearchResult {
  results: ActurisContact[];
  count: number;
  status: ContactSearchResponse['status'];
  error: string | null;
  isSearching: boolean;
  triggerSearch: (
    search: string,
    searchType: ContactSearchType,
    page?: number,
    pageSize?: number,
  ) => void;
  clearSearch: () => void;
  activeSearch: string;
  activeSearchType: ContactSearchType;
}

export const useContactSearch = ({
  enabled = true,
}: UseContactSearchOptions = {}): UseContactSearchResult => {
  const queryClient = useQueryClient();
  const [activeSearch, setActiveSearch] = useState('');
  const [activeSearchType, setActiveSearchType] =
    useState<ContactSearchType>('name');
  const [isPolling, setIsPolling] = useState(false);

  const triggerMutation = useMutation({
    mutationFn: ({
      search,
      searchType,
      page,
      pageSize,
    }: {
      search: string;
      searchType: ContactSearchType;
      page: number;
      pageSize: number;
    }) => apiTriggerContactSearch(search, searchType, page, pageSize),
    onSuccess: (_data, variables) => {
      setActiveSearch(variables.search);
      setActiveSearchType(variables.searchType);
      setIsPolling(true);
    },
  });

  const pollQuery = useQuery({
    queryKey: ['contact-search-poll', activeSearch],
    queryFn: () => apiPollContactSearch(activeSearch),
    enabled: enabled && isPolling && activeSearch.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'pending') {
        return POLL_INTERVAL;
      }
      return false;
    },
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (pollQuery.data?.status && pollQuery.data.status !== 'pending') {
      setIsPolling(false);
    }
  }, [pollQuery.data?.status]);

  const triggerSearch = useCallback(
    (
      search: string,
      searchType: ContactSearchType,
      page: number = 1,
      pageSize: number = 50,
    ) => {
      const trimmed = search.trim();
      if (!trimmed) return;

      queryClient.removeQueries({ queryKey: ['contact-search-poll'] });
      setIsPolling(false);
      setActiveSearch('');

      triggerMutation.mutate({ search: trimmed, searchType, page, pageSize });
    },
    [triggerMutation, queryClient],
  );

  const clearSearch = useCallback(() => {
    setActiveSearch('');
    setActiveSearchType('name');
    setIsPolling(false);
    queryClient.removeQueries({ queryKey: ['contact-search-poll'] });
  }, [queryClient]);

  const data = pollQuery.data;
  const isSearching =
    triggerMutation.isPending || isPolling || pollQuery.isFetching;

  const status: ContactSearchResponse['status'] =
    isPolling && !data?.status ? 'pending' : data?.status || 'idle';

  return {
    results: data?.results || [],
    count: data?.count || 0,
    status,
    error: data?.error || null,
    isSearching,
    triggerSearch,
    clearSearch,
    activeSearch,
    activeSearchType,
  };
};
